import React, { useState } from 'react';
import { startAuthentication, PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser';
import { useAuth } from './AuthContext';
import { fetchApi } from '../services/api';
import { useNavigate } from 'react-router-dom';
import logoUrl from '../assets/logo.svg';

interface LoginOptionsResponse {
  options: PublicKeyCredentialRequestOptionsJSON;
  userId: string;
}

interface LoginVerifyResponse {
  token: string;
  verified: boolean;
}

const AuthLogin: React.FC = () => {
  const [username] = useState('admin');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { options, userId } = await fetchApi<LoginOptionsResponse>('/auth/login/options', {
        method: 'POST',
        body: JSON.stringify({ username }),
      });
      let assertionResponse;
      try {
        assertionResponse = await startAuthentication({
          optionsJSON: options,
          useBrowserAutofill: true
        });
      } catch (err: any) {
        if (err.name === 'NotAllowedError') {
          setError('Authentication was cancelled or failed. Please try again.');
        } else {
          setError('Authentication failed: ' + (err.message || err));
        }
        setLoading(false);
        return;
      }
      const { token, verified } = await fetchApi<LoginVerifyResponse>('/auth/login/verify', {
        method: 'POST',
        body: JSON.stringify({ userId, assertionResponse }),
      });
      if (verified && token) {
        login(token, { id: userId, username });
        navigate('/', { replace: true });
      } else {
        setError('Login verification failed');
      }
    } catch (err: any) {
      setError('Login failed: ' + (err?.message || err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-zinc-950">
      <div className="w-full max-w-md px-6">
        <div className="rounded-2xl shadow-2xl border border-zinc-800 bg-zinc-900/90 backdrop-blur-md p-12 flex flex-col items-center">
          <img
            className="h-36 w-auto mb-8 drop-shadow-xl"
            src={logoUrl}
            alt="HAProxy UI Logo"
            draggable={false}
            style={{ maxWidth: '90%' }}
          />
          <div className="mb-8 text-zinc-400 text-center text-base font-medium">
            Passkey login only — no passwords, maximum security.
          </div>
          <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-semibold bg-indigo-700 text-zinc-100 border border-indigo-600 shadow-sm mb-10">
            admin
          </span>
          {error && <div className="w-full text-center text-red-400 bg-red-900/40 border border-red-700 rounded py-2 px-3 mb-6 font-medium animate-pulse">{error}</div>}
          <form className="w-full" onSubmit={handleLogin} autoComplete="on">
            {/* Hidden username field for accessibility and autofill */}
            <input
              type="text"
              name="username"
              autoComplete="username"
              value={username}
              readOnly
              hidden
              tabIndex={-1}
            />
            {/* Hidden fields for WebAuthn and password autofill */}
            <input type="password" name="password" autoComplete="current-password" style={{ display: 'none' }} />
            <input type="text" name="webauthn" autoComplete="webauthn" style={{ display: 'none' }} />
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-lg shadow-lg transition-all duration-150 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg>
                  Logging in...
                </span>
              ) : (
                'Login with Passkey'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AuthLogin; 